import { Entity } from '@backstage/catalog-model';
import { createApiRef } from '@backstage/core-plugin-api';

import { RepoDetails } from '../github.service';
import { GithubOrigin, GetContentData, GithubTeam, RepoLookupResult } from './unifiedGithubService.types';

export enum GithubRepositoryStatus {
  ACTIVE = 'active',
  ARCHIVED = 'archived',
  LOCKED = 'locked',
  ARCHIVED_AND_LOCKED = 'archived-and-locked',
  NOT_FOUND = 'not-found',
}

/**
 * A unified GitHub service that transparently routes requests to either the
 * GHEC or EMU authentication provider based on the entity's origin, while
 * exposing the full set of GitHub operations previously scattered across
 * {@link GithubEnterpriseClientGraph}, {@link ProjectsDetails}, and inline
 * component-level Octokit / axios calls.
 *
 * Every method on this API takes a single object parameter so that
 * call-sites are self-documenting.
 *
 * @public
 */
export interface UnifiedGithubApi {
  // ── Routing helpers ─────────────────────────────────────────────────────

  /**
   * Determines the GitHub origin (EMU or GHEC) for a given entity by
   * inspecting its annotations against the configured EMU org URL.
   */
  resolveEntityOrigin(params: { entity: Entity }): GithubOrigin;

  /**
   * Extracts the "owner/repo" project slug from an entity.  Returns
   * `undefined` when no recognisable annotation is present.
   */
  getProjectSlugForEntity(params: { entity: Entity }): string | undefined;

  // ── GraphQL – explicit origin ────────────────────────────────────────────

  /**
   * Returns `true` when the repository does **not** yet exist (i.e. the name
   * is unique / available).
   *
   * When `owner` and `origin` are omitted the service routes automatically
   * based on the active {@link EmuOrgMode} (derived from feature flags).
   * Pass them explicitly only when a specific call needs to bypass that
   * routing — e.g. a component that already knows the target org.
   *
   * In `combined` mode the check runs against both orgs concurrently and
   * returns `false` (exists) if the repo is found in **either** org.
   */
  isUniqueRepo(params: { repoName: string; owner?: string; origin?: GithubOrigin }): Promise<boolean>;

  /** Returns repository lifecycle status without changing name-availability semantics. */
  getRepositoryStatus(params: {
    repoName: string;
    owner: string;
    origin: GithubOrigin;
  }): Promise<GithubRepositoryStatus>;

  /** Returns the default branch name for a repository. */
  getDefaultBranchName(params: { owner: string; repoName: string; origin: GithubOrigin }): Promise<string>;

  /** Returns open pull-requests across all developer-experience repositories. */
  getDxWork(params: { origin: GithubOrigin }): Promise<object>;

  /**
   * Returns the GitHub teams that a user belongs to.
   *
   * When `origin` is omitted the service routes based on the active
   * {@link EmuOrgMode}. In `combined` mode, teams from both orgs are returned
   * with EMU teams suffixed with `" (emu)"`. Pass `origin` explicitly to
   * bypass flag-based routing.
   */
  getUsersGithubTeams(params: { githubUsername: string; origin?: GithubOrigin }): Promise<GithubTeam[]>;

  /**
   * Resolves the owner, origin, and existence of a repository by name.
   *
   * When `owner` or `origin` is supplied, checks only that organization. Otherwise it routes based on the active {@link EmuOrgMode} (derived from feature
   * flags), the same way {@link isUniqueRepo} does when `owner`/`origin` are
   * omitted:
   * - `ghec` (flag off): checks the GHEC org only and always reports the
   *   GHEC org/origin.
   * - `emu-org`: checks the EMU org only and always reports the EMU org/origin.
   * - `combined`: checks both orgs concurrently. If the repo exists in
   *   either org, returns that org's `owner`/`origin` with `exists: true`.
   *   If it exists in neither, returns the GHEC org/origin with
   *   `exists: false` as the creation target.
   *
   * Unlike {@link isUniqueRepo}, this distinguishes "not found" responses
   * from authentication/transport failures where feasible: a genuine error
   * (e.g. 401/403/5xx, network failure) is rethrown rather than silently
   * reported as "not found".
   */
  lookupRepo(params: { repoName: string; owner?: string; origin?: GithubOrigin }): Promise<RepoLookupResult>;

  /**
   * Checks repository-name availability for a fixed destination organization
   * (e.g. the organization a scaffolder template publishes into).
   *
   * Always checks `owner`. In `combined` mode it also checks the other
   * configured organization, preserving the cross-organization uniqueness
   * rule. When the name is taken, `owner`/`origin` identify where it was
   * found; callers must not treat them as a new destination. Non "not found"
   * failures (401/403/5xx/network) are rethrown.
   */
  lookupRepoForDestination(params: {
    repoName: string;
    owner: string;
    origin: GithubOrigin;
  }): Promise<RepoLookupResult>;

  /** Fetches full repository details (deployment info, README, etc.). */
  projectsDetailsQuery(params: { owner: string; repoName: string; origin: GithubOrigin }): Promise<RepoDetails>;

  /**
   * Derives the live deployment URL from managed-pipeline properties.
   * Pure utility – no network request.
   */
  getLink(params: { env: 'dev' | 'prod'; managedPipelineProperties: any }): string | null;

  // ── REST (Octokit) – explicit origin ─────────────────────────────────────

  /** Returns the collaborator permission level string for a user in a repo. */
  getCollaboratorPermissionLevel(params: {
    owner: string;
    repo: string;
    username: string;
    origin: GithubOrigin;
  }): Promise<string>;

  /** Returns the default branch name for a repository via the REST API. */
  getDefaultBranch(params: { owner: string; repo: string; origin: GithubOrigin }): Promise<string>;

  /**
   * Returns `true` when the specified file path exists in the default branch
   * of the repository.
   */
  checkFileExists(params: { owner: string; repo: string; filePath: string; origin: GithubOrigin }): Promise<boolean>;

  // ── Entity-aware convenience wrappers ────────────────────────────────────

  /** {@link isUniqueRepo} with origin resolved from the entity. */
  isUniqueRepoForEntity(params: { entity: Entity }): Promise<boolean>;

  /** {@link getDefaultBranchName} with owner/repo and origin resolved from the entity. */
  getDefaultBranchNameForEntity(params: { entity: Entity }): Promise<string>;

  /** {@link projectsDetailsQuery} with owner/repo and origin resolved from the entity. */
  projectsDetailsQueryForEntity(params: { entity: Entity }): Promise<RepoDetails>;

  /** {@link getCollaboratorPermissionLevel} with owner/repo and origin resolved from the entity. */
  getCollaboratorPermissionLevelForEntity(params: { entity: Entity; username: string }): Promise<string>;

  /** Returns the current signed-in GitHub username for the entity's routed origin. */
  getCurrentUsernameForEntity(params: { entity: Entity }): Promise<string>;

  /** {@link getDefaultBranch} (REST) with owner/repo and origin resolved from the entity. */
  getDefaultBranchForEntity(params: { entity: Entity }): Promise<string>;

  /** {@link checkFileExists} with owner/repo and origin resolved from the entity. */
  checkFileExistsForEntity(params: { entity: Entity; filePath: string }): Promise<boolean>;

  // ── File & property helpers ───────────────────────────────────────────────

  /**
   * Fetches the raw text content of a file from the HEAD of the default branch.
   * Returns `undefined` when the file does not exist.
   */
  fetchFileContent(params: {
    owner: string;
    repo: string;
    filePath: string;
    origin: GithubOrigin;
  }): Promise<string | undefined>;

  /** Returns the list of custom property values for a repository. */
  listRepoCustomProperties(params: {
    owner: string;
    repo: string;
    origin: GithubOrigin;
  }): Promise<{ property_name: string; value: any }[]>;

  /** Archives a repository (sets `archived: true`). */
  archiveRepo(params: { owner: string; repo: string; origin: GithubOrigin }): Promise<void>;

  /** Permanently deletes a repository. */
  deleteRepo(params: { owner: string; repo: string; origin: GithubOrigin }): Promise<void>;

  // ── Write-path operations ────────────────────────────────────────────────

  /**
   * Creates a new branch in a repository from the tip of an existing ref
   * (e.g. `"heads/main"`).
   */
  createBranch(params: { owner: string; repo: string; branchName: string; fromRef: string }): Promise<void>;

  /**
   * Creates a new file in a repository branch.
   * Fails with a 422 if the file already exists — use {@link updateFile} for existing files.
   * Returns the new blob SHA of the committed file.
   */
  createFile(params: {
    owner: string;
    repo: string;
    path: string;
    message: string;
    content: string;
    branch: string;
  }): Promise<{ sha: string }>;

  /**
   * Updates an existing file in a repository branch.
   * Requires the current blob `sha` — use {@link createFile} for new files.
   * Returns the new blob SHA of the committed file.
   */
  updateFile(params: {
    owner: string;
    repo: string;
    path: string;
    message: string;
    content: string;
    sha: string;
    branch: string;
  }): Promise<{ sha: string }>;

  /**
   * Opens a pull request and returns its number, HTML URL, and the user who opened it.
   */
  openPullRequest(params: {
    owner: string;
    repo: string;
    title: string;
    head: string;
    base: string;
    body?: string;
  }): Promise<{ number: number; html_url: string; user: { login: string } | null }>;

  /**
   * Requests individual and/or team reviewers on an open pull request.
   */
  requestReviewers(params: {
    owner: string;
    repo: string;
    pullNumber: number;
    reviewers: string[];
    teamReviewers: string[];
  }): Promise<void>;

  /**
   * Triggers a `repository_dispatch` event on a repository.
   * Requires the `repo` OAuth scope on the token.
   */
  repositoryDispatch(params: {
    owner: string;
    repo: string;
    eventType: string;
    clientPayload: Record<string, unknown>;
  }): Promise<void>;

  // ── Extended read helpers ────────────────────────────────────────────────

  /**
   * Returns the raw GitHub content data for a file. Callers are responsible
   * for type-narrowing and error handling. The `sha` value is required by
   * {@link updateFile} when updating an existing file.
   */
  getFileInfo(params: { owner: string; repo: string; filePath: string; ref: string }): Promise<GetContentData>;

  /** {@link fetchFileContent} with owner/repo and origin resolved from the entity. */
  fetchFileContentForEntity(params: { entity: Entity; filePath: string }): Promise<string | undefined>;

  /** {@link listRepoCustomProperties} with owner/repo and origin resolved from the entity. */
  listRepoCustomPropertiesForEntity(params: { entity: Entity }): Promise<{ property_name: string; value: any }[]>;

  /** {@link archiveRepo} with owner/repo and origin resolved from the entity. */
  archiveRepoForEntity(params: { entity: Entity }): Promise<void>;

  /** {@link deleteRepo} with owner/repo and origin resolved from the entity. */
  deleteRepoForEntity(params: { entity: Entity }): Promise<void>;
}

export const unifiedGithubApiRef = createApiRef<UnifiedGithubApi>({
  id: 'plugin.unified-github.service',
});
